export interface SalesforceImportJob {
  job_type: string;
  status: 'active' | 'completed' | 'failed';
  items_processed: number;
  items_failed: number;
}

export interface SalesforceInstallResponse {
  url: string;
}

export interface SalesforceStatusResponse {
  connected: boolean;
  /**
   * Whether this org has supplied its External Client App consumer key/secret.
   * Distinct from `connected`: an org can be un-connected either because it has
   * credentials and hasn't authorised yet (show Connect) or because it has none
   * at all (show the setup step). Always present, in both branches.
   */
  credentials_configured: boolean;
  /**
   * The absolute OAuth callback URL the backend builds from `settings.SITE_URL`
   * (or the local-proxy host). This is the authoritative `redirect_uri` the
   * Salesforce connected app must register — it can differ from the SPA origin,
   * so the setup form must display this value rather than deriving one from
   * `window.location.origin`. Present in both the connected and not-connected
   * responses.
   */
  callback_url?: string;
  sf_org_id?: string;
  instance_url?: string;
  is_sandbox?: boolean;
  bulk_completed_at?: string | null;
  import_jobs?: SalesforceImportJob[];
  total_objects_synced?: number;
}

export const SALESFORCE_OBJECT_TYPES = [
  { id: 'account', label: 'Accounts', entityType: 'ahoy_company' },
  { id: 'contact', label: 'Contacts', entityType: 'ahoy_contact' },
  { id: 'opportunity', label: 'Opportunities', entityType: 'ahoy_deal' },
] as const;

export type SalesforceObjectType =
  (typeof SALESFORCE_OBJECT_TYPES)[number]['id'];

export interface SalesforceFieldMapping {
  id: number;
  object_type: SalesforceObjectType;
  salesforce_field: string;
  ahoy_property: string | null;
  sync_direction:
    | 'bidirectional'
    | 'ahoy_to_external'
    | 'external_to_ahoy'
    | 'skipped';
  conflict_resolution: 'ahoy_wins' | 'external_wins' | 'most_recent' | null;
  salesforce_field_type: string;
  is_required_in_salesforce: boolean;
}

export interface SalesforceFieldMappingsResponse {
  mappings: SalesforceFieldMapping[];
}

export interface AhoyPropertyOption {
  name: string;
  label: string;
}

export type LoginHostSelection = 'production' | 'sandbox' | 'custom';
